import { useCallback, useEffect, useState } from "react";
import {
  GALLERY_COLUMNS_STORAGE_KEY,
  GALLERY_DESKTOP_QUERY,
  galleryCardSize,
  parseColumnPrefs,
  type GalleryColumnPrefs,
  type GalleryColumns,
  type GalleryScreen,
} from "@/lib/galleryDensity";

function readPrefs(): GalleryColumnPrefs {
  try {
    return parseColumnPrefs(window.localStorage.getItem(GALLERY_COLUMNS_STORAGE_KEY));
  } catch {
    return parseColumnPrefs(null);
  }
}

function currentScreen(): GalleryScreen {
  return typeof window !== "undefined" && window.matchMedia(GALLERY_DESKTOP_QUERY).matches ? "desktop" : "mobile";
}

/** The gallery column choice for this screen size, remembered on this device. */
export function useGalleryColumns() {
  const [prefs, setPrefs] = useState<GalleryColumnPrefs>(readPrefs);
  const [screen, setScreen] = useState<GalleryScreen>(currentScreen);

  useEffect(() => {
    const query = window.matchMedia(GALLERY_DESKTOP_QUERY);
    const update = () => setScreen(query.matches ? "desktop" : "mobile");
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  const setColumns = useCallback((columns: GalleryColumns) => {
    setPrefs((current) => {
      const next = { ...current, [screen]: columns };
      try {
        window.localStorage.setItem(GALLERY_COLUMNS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Private mode or blocked storage: the choice lasts for this visit only.
      }
      return next;
    });
  }, [screen]);

  const columns = prefs[screen];
  return { columns, screen, cardSize: galleryCardSize(columns, screen), setColumns };
}
